import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillTransfer, faArrowDown, faArrowUp, faListAlt, faCheckCircle, faRotateRight
} from '@fortawesome/free-solid-svg-icons';
import UiButton from '../../components/ui/UiButton';
import UiPageHeader from '../../components/ui/UiPageHeader';
import UiFilterPanel from '../../components/ui/UiFilterPanel';
import UiStatsGrid from '../../components/ui/UiStatsGrid';
import DataTable, { Column } from '../../components/shared/DataTable';
import type { StatCardDef } from '../../components/ui';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Bank {
  id: string;
  name: string;
  bank_code: string;
  account_number: string;
}

interface Transaction {
  id: string;
  bank_id: string;
  transaction_type: 'credit' | 'debit' | 'in' | 'out';
  amount: number;
  from_account: string | null;
  to_account: string | null;
  balance: number | null;
  transaction_date: string;
  reference_id: string | null;
  sender: string | null;
  is_matched: boolean;
  matched_order_id: string | null;
  banks: { name: string; bank_code: string } | null;
}

const fmt = (n: number) =>
  n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BKK = 'Asia/Bangkok';
const getTodayStr = () => dayjs().tz(BKK).format('YYYY-MM-DD');

const isCredit = (type: string) => type === 'credit' || type === 'in';

const bankCodeColors: Record<string, string> = {
  SCB: 'bg-purple-100 text-purple-700',
  KBANK: 'bg-green-100 text-green-700',
  BBL: 'bg-blue-100 text-blue-700',
  KTB: 'bg-sky-100 text-sky-700',
  BAY: 'bg-yellow-100 text-yellow-700',
  GSB: 'bg-pink-100 text-pink-700',
};

const AgentTransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [banks, setBanks] = useState<Bank[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [summary, setSummary] = useState({ total_credit: 0, credit_count: 0, total_debit: 0, debit_count: 0, matched_count: 0 });
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [dateFrom, setDateFrom] = useState(getTodayStr());
  const [dateTo, setDateTo] = useState(getTodayStr());
  const [selectedBankId, setSelectedBankId] = useState('');

  useEffect(() => {
    const t = getTodayStr();
    fetchSummary(t, t, '');
    fetchTransactions(1, t, t, '');
  }, []);

  // fetch summary — โหลดเฉพาะตอน search/reset/refresh (ไม่โหลดซ้ำตอนเปลี่ยนหน้า)
  const fetchSummary = useCallback(async (from: string, to: string, bankId: string) => {
    setSummaryLoading(true);
    try {
      const res = await merchantApi.get('/agent/transactions', {
        params: { date_from: from, date_to: to, bank_id: bankId || undefined, page: 1, limit: 1, include_summary: 'true' }
      });
      if (res.data.success && res.data.data.summary) {
        setSummary(res.data.data.summary);
      }
    } catch {}
    finally { setSummaryLoading(false); }
  }, []);

  // fetch transactions page — ไม่โหลด summary ซ้ำ
  const fetchTransactions = useCallback(async (
    p: number, from: string, to: string, bankId: string, limit?: number
  ) => {
    setLoading(true);
    try {
      const res = await merchantApi.get('/agent/transactions', {
        params: { date_from: from, date_to: to, bank_id: bankId || undefined, page: p, limit: limit ?? pageSize }
      });
      if (res.data.success) {
        setTransactions(res.data.data.transactions);
        setBanks(res.data.data.banks);
        setTotal(res.data.data.total);
        setPage(p);
      }
    } catch (error: any) {
      if (error.response?.status === 401) navigate('/agent/login');
      else toast.error('ดึงข้อมูลไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [navigate, pageSize]);

  const handleSearch = () => {
    fetchSummary(dateFrom, dateTo, selectedBankId);
    fetchTransactions(1, dateFrom, dateTo, selectedBankId);
  };
  const handleReset = () => {
    const t = getTodayStr();
    setDateFrom(t); setDateTo(t); setSelectedBankId('');
    fetchSummary(t, t, '');
    fetchTransactions(1, t, t, '');
  };
  const handleRefreshSummary = () => fetchSummary(dateFrom, dateTo, selectedBankId);

  const stats: StatCardDef[] = [
    {
      icon: faListAlt,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'รายการทั้งหมด',
      value: <span style={{ color: 'var(--theme-primary)' }}>{total.toLocaleString()}</span>,
    },
    {
      icon: faArrowDown,
      iconClassName: 'bg-green-100 text-green-600',
      label: 'ฝาก (Credit)',
      value: <span className="text-green-600">฿{fmt(summary.total_credit)}</span>,
      subValue: <span className="text-xs text-gray-400">{summary.credit_count.toLocaleString()} รายการ</span>,
    },
    {
      icon: faArrowUp,
      iconClassName: 'bg-amber-100 text-amber-600',
      label: 'ถอน (Debit)',
      value: <span className="text-amber-600">฿{fmt(summary.total_debit)}</span>,
      subValue: <span className="text-xs text-gray-400">{summary.debit_count.toLocaleString()} รายการ</span>,
    },
    {
      icon: faCheckCircle,
      iconClassName: 'bg-indigo-100 text-indigo-600',
      label: 'จับคู่แล้ว',
      value: <span className="text-indigo-600">{summary.matched_count.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400">จาก {total.toLocaleString()} รายการ</span>,
    },
  ];

  const columns: Column<Transaction>[] = [
    {
      key: 'transaction_date',
      header: 'วันที่/เวลา',
      width: '130px',
      render: (tx) => (
        <div>
          <p className="text-sm text-gray-700 font-medium">{dayjs(tx.transaction_date).format('DD/MM/YYYY')}</p>
          <p className="text-xs text-gray-400">{dayjs(tx.transaction_date).format('HH:mm:ss')}</p>
        </div>
      ),
    },
    {
      key: 'bank',
      header: 'ธนาคาร',
      render: (tx) => tx.banks ? (
        <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${bankCodeColors[tx.banks.bank_code] || 'bg-gray-100 text-gray-600'}`}>
          {tx.banks.bank_code}
        </span>
      ) : <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: 'transaction_type',
      header: 'ประเภท',
      render: (tx) => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${
          isCredit(tx.transaction_type)
            ? 'bg-green-100 text-green-700'
            : 'bg-amber-100 text-amber-700'
        }`}>
          <FontAwesomeIcon icon={isCredit(tx.transaction_type) ? faArrowDown : faArrowUp} className="text-[10px]" />
          {isCredit(tx.transaction_type) ? 'ฝาก' : 'ถอน'}
        </span>
      ),
    },
    {
      key: 'amount',
      header: 'จำนวนเงิน',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (tx) => (
        <span className={`font-bold ${isCredit(tx.transaction_type) ? 'text-green-600' : 'text-amber-600'}`}>
          ฿{fmt(Number(tx.amount))}
        </span>
      ),
    },
    {
      key: 'from_account',
      header: 'ต้นทาง',
      render: (tx) => (
        <div>
          {tx.sender && <p className="text-sm text-gray-700 truncate max-w-[140px]">{tx.sender}</p>}
          {tx.from_account && <p className="text-xs text-gray-400">{tx.from_account}</p>}
          {!tx.sender && !tx.from_account && <span className="text-gray-400 text-xs">—</span>}
        </div>
      ),
    },
    {
      key: 'reference_id',
      header: 'อ้างอิง',
      render: (tx) => tx.reference_id
        ? <span className="text-xs text-gray-500 font-mono">{tx.reference_id}</span>
        : <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: 'is_matched',
      header: 'จับคู่',
      render: (tx) => (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${
          tx.is_matched ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
        }`}>
          <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" />
          {tx.is_matched ? 'แล้ว' : 'ยังไม่'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <UiPageHeader
        icon={faMoneyBillTransfer}
        title="Transactions"
        subtitle={`รายการธุรกรรมของธนาคารที่ผูกไว้ • ${total.toLocaleString()} รายการ`}
      />

      <UiFilterPanel
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
      >
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">ธนาคาร</label>
          <select
            value={selectedBankId}
            onChange={e => setSelectedBankId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400"
          >
            <option value="">ทุกธนาคาร</option>
            {banks.map(bank => (
              <option key={bank.id} value={bank.id}>
                [{bank.bank_code}] {bank.name} — {bank.account_number}
              </option>
            ))}
          </select>
        </div>
      </UiFilterPanel>

      <div className="flex justify-end mb-3">
        <button
          onClick={handleRefreshSummary}
          disabled={summaryLoading}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-gray-400 hover:text-white transition-all disabled:opacity-40"
          style={summaryLoading ? undefined : { background: 'none' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--theme-primary-gradient)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
        >
          <FontAwesomeIcon icon={faRotateRight} className={summaryLoading ? 'animate-spin' : ''} />
          รีเฟรช
        </button>
      </div>
      <UiStatsGrid cards={stats} loading={summaryLoading} cols={4} />

      <DataTable
        data={transactions}
        columns={columns}
        loading={loading}
        keyExtractor={(tx) => tx.id}
        emptyMessage="ไม่มีรายการธุรกรรมในช่วงเวลาที่เลือก"
        striped
        hoverable
        serverPagination={{
          currentPage: page,
          totalItems: total,
          pageSize,
          pageSizeOptions: [25, 50, 100],
          onPageChange: (p) => fetchTransactions(p, dateFrom, dateTo, selectedBankId),
          onPageSizeChange: (size) => {
            setPageSize(size);
            fetchTransactions(1, dateFrom, dateTo, selectedBankId, size);
          },
        }}
      />
    </div>
  );
};

export default AgentTransactionsPage;
